import { useEffect, useRef, useState } from 'react';

import type { CreditSimulationPartialProps } from 'blocks/CreditSimulation';
import ErrorBoundary from 'components/ErrorBoundary';
import { PartialPopover } from 'components/Popover';
import {
	PRODUCT_DETAILS_IMPORTANT_INFO_ID,
	PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID,
} from 'constants/ids';
import {
	useCart,
	useEffectOnce,
	useEngagementGoals,
	useFeatureToggle,
	useListState,
	useSafeFieldValues,
	useSitecoreContext,
	useTemporaryStoredValue,
	useValueChangeEffect,
	useWishlist,
} from 'hooks';
import {
	type GTMItemListId,
	type GTMItemListName,
	useOpenCreditSimulationGTM,
	useProductGTMEvents,
	useProductListGTMEvents,
	useProfilingGTM,
	useViewItemGTM,
	useViewItemListGTM,
} from 'hooks/gtm';
import { useHash } from 'hooks/navigation';
import type { Product, VariantId } from 'models/product';
import type { SitecoreBlockProps } from 'models/sitecore';
import { is } from 'utils/helpers';

import ProductDetailsBlock from './ProductDetails';
import ProductDetailsContext, {
	useBundleContext,
	useProductPrintContext,
} from './ProductDetailsContext';

interface Props extends SitecoreBlockProps {
	/** Product data following the product model. */
	fields: Product;
}

function ProductDetails({ fields }: Props) {
	const product = fields;
	const { sizeGuideUrl } = product;
	const content = useSafeFieldValues(fields.content);

	const { onlineCommerceEnabled, wishlistEnabled } = useFeatureToggle();

	const { sitecoreContext } = useSitecoreContext();
	const { customizationId } = useProductPrintContext();
	const { isLoading: bundleProductsIsLoading, items: bundleProducts } =
		useBundleContext();

	const { addToCart } = useCart();
	const { addToWishlist } = useWishlist();

	const {
		addItems: addSelectedAccessoryUpsellId,
		clearItems: clearSelectedAccessoryUpsellIds,
		items: selectedAccessoryUpsellIds,
		removeItems: removeSelectedAccessoryUpsellId,
	} = useListState<VariantId>();

	const [isSizeGuidePopoverOpen, setIsSizeGuidePopoverOpen] = useState(false);
	const [
		manufacturerInformationPopoverOpen,
		setManufacturerInformationPopoverOpen,
	] = useState(false);
	const [isAboutBrandOpen, setIsAboutBrandOpen] = useState(false);
	const [isTechnicalAttributesOpen, setIsTechnicalAttributesOpen] =
		useState(false);
	const [isImportantInfoOpen, setIsImportantInfoOpen] = useState(false);
	const [creditSimulationPopoverIsOpen, setCreditSimulationPopoverIsOpen] =
		useState(false);

	const urlHash = useHash();
	useEffectOnce(() => {
		if (urlHash === PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID) {
			setIsTechnicalAttributesOpen(true);
		}
		if (urlHash === PRODUCT_DETAILS_IMPORTANT_INFO_ID) {
			setIsImportantInfoOpen(true);
		}
	});

	const { getStoredValue } = useTemporaryStoredValue('GTM_LIST_DATA');
	const { sendViewItemEventOnce } = useViewItemGTM();
	const { sendViewItemListEvent } = useViewItemListGTM();
	const { sendProfilingAddEvent } = useProfilingGTM();
	const { sendOpenCreditSimulationEvent } = useOpenCreditSimulationGTM();
	const { sendAddToCartEvent, sendAddToWishlistEvent } = useProductGTMEvents();
	const { sendEngagement } = useEngagementGoals();

	const parentItemListData = useRef<{
		gtmItemListId: GTMItemListId;
		gtmItemListName: GTMItemListName;
	}>(undefined);
	const {
		gtmItemListId: accessoriesUpsellGtmItemListId,
		gtmItemListName: accessoriesUpsellGtmItemListName,
		sendAddToCartEvent: sendAccessoriesUpsellAddToCartEvent,
		sendAddToWishlistEvent: sendAccessoriesUpsellAddToWishlistEvent,
	} = useProductListGTMEvents(
		'pdp_accessories_upsale',
		`List: Accessories upsale ${product.id}`,
	);

	useEffect(() => {
		const stored = getStoredValue();
		if (stored) {
			parentItemListData.current = stored;
		}
		sendViewItemEventOnce({
			product,
			category: product.category1Name,
			itemListId: parentItemListData?.current?.gtmItemListId,
			itemListName: parentItemListData?.current?.gtmItemListName,
		});
		product.campaigns?.forEach((campaign) => {
			sendViewItemListEvent({
				itemListId: 'pdp_mix_match',
				itemListName: `Mix and Match: [${campaign.discountType}] - [${campaign.title}] ([${campaign.id}])`,
				products: [product],
				pageSize: 1,
			});
		});
		// The product object could in theory change while the ID stays the same,
		// in which case additional events would not be desired.
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [product.id]);

	useValueChangeEffect(bundleProductsIsLoading, (prevIsLoading) => {
		if (
			prevIsLoading &&
			!bundleProductsIsLoading &&
			is.arrayWithLength(bundleProducts)
		) {
			sendViewItemListEvent({
				itemListId: 'pdp_bundle_products',
				itemListName: `List: Bundle Contents ${product.id}`,
				products: bundleProducts,
				pageSize: bundleProducts.length,
			});
		}
	});

	return (
		<>
			<ProductDetailsBlock
				product={product}
				key={product.id}
				isTechnicalAttributesOpen={isTechnicalAttributesOpen}
				isImportantInfoOpen={isImportantInfoOpen}
				onImportantInfoClick={() => {
					setIsImportantInfoOpen((current) => !current);
				}}
				onSafetyInstructionsClick={() => {
					setIsImportantInfoOpen(true);
				}}
				isAboutBrandOpen={isAboutBrandOpen}
				onAboutBrandClick={() => setIsAboutBrandOpen((current) => !current)}
				onTechnicalAttributesClick={() => {
					setIsTechnicalAttributesOpen((current) => !current);
				}}
				onCampaignPackageAddToWishlistClick={
					wishlistEnabled
						? async (campaign) => {
								await addToWishlist(
									campaign.rows.map(({ quantity, variantId }) => ({
										variantId,
										quantity,
									})),
									{ returnAddedProducts: true, showToast: true },
								);
								// TODO: multi item gtm wishlist event
							}
						: undefined
				}
				onCampaignPackageAddToCartClick={
					onlineCommerceEnabled
						? async (campaign) => {
								const itemListId: GTMItemListId = 'pdp_mix_match';
								const itemListName: GTMItemListName = `Mix and Match: [${campaign.discountType}] - [${campaign.title}] ([${campaign.id}])`;
								const cart = await addToCart(
									campaign.rows.map(({ quantity, variantId }) => ({
										variantId,
										quantity,
										metaData: {
											gtmItemListId: itemListId,
											gtmItemListName: itemListName,
										},
									})),
									{ returnAddedProducts: true, showToast: true },
								);
								sendAddToCartEvent({
									cartId: cart.id,
									items: campaign.rows.map((row) => ({
										product: { ...row, priceMissing: true },
										quantity: row.quantity,
										itemListId,
										itemListName,
									})),
								});
								sendEngagement({ type: 'addArticleToCart' });
							}
						: undefined
				}
				onMainPurchaseButtonClick={async () => {
					const cart = await addToCart(
						[
							{
								variantId: product.id,
								customizationId,
								metaData: parentItemListData?.current
									? {
											gtmItemListId: parentItemListData?.current?.gtmItemListId,
											gtmItemListName:
												parentItemListData?.current?.gtmItemListName,
										}
									: undefined,
							},
							...selectedAccessoryUpsellIds.map((id) => ({
								variantId: id,
								metaData: {
									gtmItemListId: accessoriesUpsellGtmItemListId,
									gtmItemListName: accessoriesUpsellGtmItemListName,
								},
							})),
						],
						{
							returnAccessories: true,
							returnAddedProducts: true,
							showPopover: true,
						},
					);
					sendAddToCartEvent({
						cartId: cart.id,
						items: {
							product,
							quantity: 1,
							itemListId: parentItemListData?.current?.gtmItemListId,
							itemListName: parentItemListData?.current?.gtmItemListName,
						},
					});
					sendEngagement({ type: 'addArticleToCart' });

					if (customizationId) {
						sendProfilingAddEvent(product.id);
					}
					selectedAccessoryUpsellIds.forEach((id) => {
						sendAccessoriesUpsellAddToCartEvent(cart.id, { id });
					});
					clearSelectedAccessoryUpsellIds();
				}}
				onMainWishlistButtonClick={async () => {
					await addToWishlist(
						[
							{ variantId: product.id },
							...selectedAccessoryUpsellIds.map((id) => ({ variantId: id })),
						],
						{
							showPopover: true,
							returnAccessories: true,
							returnAddedProducts: true,
						},
					);
					// TODO: multi item gtm wishlist event
					sendAddToWishlistEvent({
						product,
						quantity: 1,
					});
					sendEngagement({ type: 'addArticleToWishlist' });
					selectedAccessoryUpsellIds.forEach((id) => {
						sendAccessoriesUpsellAddToWishlistEvent({ id });
					});
					clearSelectedAccessoryUpsellIds();
				}}
				onSizeGuideClick={() => {
					setIsSizeGuidePopoverOpen(true);
				}}
				onManufacturerInformationClick={() => {
					setManufacturerInformationPopoverOpen(true);
				}}
				onCreditSimulationClick={() => {
					setCreditSimulationPopoverIsOpen(true);
					sendOpenCreditSimulationEvent();
				}}
				selectedAccessoryUpsellIds={selectedAccessoryUpsellIds}
				onSelectedAccessoryUpsellAdd={addSelectedAccessoryUpsellId}
				onSelectedAccessoryUpsellRemove={removeSelectedAccessoryUpsellId}
			/>
			{sitecoreContext.metaData?.structuredData && (
				<script
					id="product-structured-data"
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(sitecoreContext.metaData?.structuredData),
					}}
				/>
			)}
			<PartialPopover
				heading={content.sizeGuidePopoverTitle}
				isOpen={isSizeGuidePopoverOpen}
				partial={sizeGuideUrl}
				partialQueryVars={{
					productId: product.productId,
				}}
				onClose={() => setIsSizeGuidePopoverOpen(false)}
			/>
			<PartialPopover
				heading={content.manufacturerInformationPopoverTitle}
				isOpen={manufacturerInformationPopoverOpen}
				partial="manufacturerInfo"
				partialQueryVars={{
					productId: product.productId,
				}}
				onClose={() => setManufacturerInformationPopoverOpen(false)}
			/>
			<PartialPopover<CreditSimulationPartialProps>
				heading={content.creditSimulationPopoverTitle}
				isOpen={creditSimulationPopoverIsOpen}
				partial="creditSimulation"
				partialProps={{ price: product.displayPrice?.priceIncVat?.value ?? 0 }}
				onClose={() => setCreditSimulationPopoverIsOpen(false)}
			/>
		</>
	);
}
ProductDetails.displayName = 'ProductDetails';

export default function ProductDetailsInstance(props: Props) {
	if (!props?.fields) {
		return null;
	}
	const product = props.fields;
	const {
		hasAccessories,
		hasBelongsTo,
		hasBelongsToBundle,
		hasSpareParts,
		isCustomizable,
		salesAttributes,
		variants,
	} = product;
	return (
		<ErrorBoundary isPageWidth>
			<ProductDetailsContext
				key={product.id}
				variantId={product.id}
				productId={product.productId}
				variantIds={variants?.map((variant) => variant.id)}
				productType={product.productType}
				isSellable={salesAttributes?.isSellable}
				reviewScore={product.reviewScore}
				isCustomizable={isCustomizable}
				hasAccessories={hasAccessories}
				hasBelongsTo={hasBelongsTo}
				hasBelongsToBundle={hasBelongsToBundle}
				hasSpareParts={hasSpareParts}
			>
				<ProductDetails {...props} />
			</ProductDetailsContext>
		</ErrorBoundary>
	);
}
ProductDetailsInstance.displayName = 'ProductDetailsInstance';
